import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ReactNode } from "react";
import EdgeColors from "@/components/EdgeColors";

interface CheckoutCardProps {
  /**
   * Header content (usually a title)
   */
  backButton?: boolean;
  onBack?: () => void;
  backLabel?: string;
  compactMobileBackButton?: boolean;
  backIconOnly?: boolean;
  header: ReactNode;

  /**
   * Main card content (order summary, payment details, buttons, etc.)
   */
  children: ReactNode;

  /**
   * Optional additional classes for the header section
   * Default: "bg-mathyellow bg-opacity-20"
   */
  headerClassName?: string;

  /**
   * Remove margins, max-width, rounded corners and shadow
   * so the card fills the entire screen width.
   * Used for mobile checkout where the card is the only content.
   */
  fullWidth?: boolean;

  /**
   * Blend into bg-achtergrond with no header styling.
   * Defaults to true when fullWidth is set (cards in WaveSections/marketing pages).
   * Set to false explicitly for standalone fullWidth cards (e.g. PersonalizationForm).
   */
  embedded?: boolean;

  /**
   * Extra content rendered below the white card on bg-achtergrond.
   * Only used in fullWidth mobile mode (e.g. FAQ section).
   */
  footer?: ReactNode;

  /**
   * Content pinned to the bottom of the screen in mobile fullWidth mode.
   * Renders as a sticky white bar with shadow above the safe area.
   */
  stickyBottom?: ReactNode;

  /**
   * Hide the BookAndFeaturesShowcase in mobile layout.
   * Card starts directly below the topbar.
   */
  hideShowcase?: boolean;

  /**
   * Desktop max-width for the card shell.
   */
  desktopWidth?: "default" | "wide";

  /**
   * When true, play slide-down exit animation on the card.
   */
  isExiting?: boolean;

  /**
   * Called when the exit animation finishes.
   */
  onExitAnimationEnd?: () => void;
}

/**
 * Reusable checkout card component with consistent styling
 *
 * Mobile (fullWidth): clean topbar + bg-achtergrond + white rounded card
 * Desktop: white card with yellow header, rounded corners, shadow
 */
export default function CheckoutCard({
  backButton = false,
  onBack,
  backLabel = "Terug",
  compactMobileBackButton = false,
  backIconOnly = false,
  header,
  children,
  headerClassName = "bg-mathyellow bg-opacity-20",
  fullWidth = false,
  embedded,
  footer,
  stickyBottom,
  hideShowcase = false,
  desktopWidth = "default",
  isExiting = false,
  onExitAnimationEnd
}: CheckoutCardProps) {
  const isMobileCard = fullWidth && !!onBack;
  const hasMobileStickyHeader = isMobileCard;
  void footer;
  void stickyBottom;
  void hideShowcase;
  void isExiting;
  void onExitAnimationEnd;

  // Mobile-card edge-color override: tint the URL bar / status bar to the card's
  // header color so the notch area matches. Stacks on top of the route-level
  // EdgeColors and pops on unmount.
  const mobileCardEdge = isMobileCard ? (
    <EdgeColors
      top={hasMobileStickyHeader ? "#fbf4e0" : "#ffffff"}
      bottom="#ffffff"
    />
  ) : null;

  // Embedded mode: card blends into bg-achtergrond (marketing pages inside WaveSections)
  // Defaults to true when fullWidth, unless explicitly set to false
  const isEmbedded = (embedded ?? fullWidth) && fullWidth && !isMobileCard;

  // Mobile full-screen card with sticky header.
  if (hasMobileStickyHeader) {
    const containerClass = `overflow-hidden bg-white min-h-dvh`;
    const mobileBackButtonClassName = backIconOnly
      ? "inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-lg text-gray-700 transition-colors hover:bg-white/80 active:bg-white"
      : compactMobileBackButton
        ? "mr-3 inline-flex h-8 shrink-0 items-center gap-1.5 rounded-md border border-gray-300 bg-white px-2.5 text-xs font-bold text-gray-800 shadow-sm transition-all hover:bg-gray-50 active:bg-gray-100"
        : "inline-flex h-10 shrink-0 items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 text-sm font-bold text-gray-800 shadow-sm transition-all hover:bg-gray-50 active:bg-gray-100";
    const mobileBackIconClassName = backIconOnly
      ? "text-xl text-gray-800 [stroke:currentColor] [stroke-width:16]"
      : compactMobileBackButton
        ? "text-gray-700 text-sm"
        : "text-gray-700 text-base";

    return (
      <div className={containerClass}>
        {mobileCardEdge}
        <div className="sticky top-0 z-50 border-b border-gray-200 bg-white">
          <div className={`${headerClassName} pt-safe-top`}>
            <div className="px-4 pt-6 pb-6 relative flex items-center justify-start">
              {backButton && (
                <button
                  onClick={onBack}
                  className={mobileBackButtonClassName}
                  aria-label="Terug"
                >
                  <FontAwesomeIcon
                    icon={faArrowLeft}
                    className={mobileBackIconClassName}
                  />
                  {!backIconOnly && backLabel}
                </button>
              )}
              <span className="flex-1">{header}</span>
            </div>
          </div>
        </div>
        <div className="px-4 md:px-6 py-6 md:py-8 flex flex-col items-center">
          {children}
        </div>
      </div>
    );
  }

  // ── Desktop or fullWidth without back button ──
  const desktopWidthClass =
    desktopWidth === "wide" ? "md:max-w-xl" : "md:max-w-lg";
  const containerClass = fullWidth
    ? `overflow-hidden ${isEmbedded ? "bg-achtergrond" : "bg-white"}`
    : `m-4 max-w-sm ${desktopWidthClass} bg-white rounded-lg md:rounded-2xl shadow-md md:shadow-lg overflow-hidden md:border border-blue-100`;

  return (
    <div className={containerClass}>
      {/* Header Section */}
      <div
        className={
          isEmbedded ? "" : `border-b border-gray-200 ${headerClassName}`
        }
      >
        {backButton && onBack && (
          <button
            onClick={onBack}
            className={
              backIconOnly
                ? "ml-2 mt-2 flex h-11 w-11 items-center justify-center rounded-full text-lg text-gray-700 transition-colors hover:bg-white/70 active:bg-white"
                : "w-20 h-8 bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 transition-all font-medium text-sm flex items-center justify-center gap-2 -mb-2"
            }
            aria-label="Terug"
          >
            <FontAwesomeIcon
              icon={faArrowLeft}
              className="text-xl text-gray-800 [stroke:currentColor] [stroke-width:16]"
            />
            {!backIconOnly && backLabel}
          </button>
        )}
        <div className="px-4 md:px-10 pt-6 pb-6">{header}</div>
      </div>

      {/* Body Section */}
      <div
        className={`px-4 md:px-6 py-6 md:py-8 flex flex-col items-center ${isEmbedded ? "pb-10" : ""}`}
      >
        {children}
      </div>
    </div>
  );
}
